import { FormGroup } from '@angular/forms';
import {
  FormRule,
  FormRuleEffect,
  FormRuleFactory,
  RuleAction,
} from 'src/app/shared/forms';
import { SubmissionConstants } from '../../models/constants/submissions-constants';
import { DesignChoices, DesignPointsService } from './design-points-service';
import { ZeroEnergyEligibleRules } from './zeroEnergyEligibleRules';
import { HeatPumpDocumentRules } from './heatPumpDocumentRules';

export class SbraV3CustomRules {
  constructor(
    private ruleFact: FormRuleFactory,
    private submissionsConst: SubmissionConstants,
  ) {}

  getCustomRules(subValues: FormGroup): FormRule[] {
    this.setEnergyEfficiencyValue(subValues);
    return [
      ...this.getShouldShowDownloadInvoiceRules(subValues),
      ...new ZeroEnergyEligibleRules(
        this.ruleFact,
        this.submissionsConst,
      ).getSbraV3CustomRules(subValues),
      ...new HeatPumpDocumentRules(
        this.ruleFact,
        this.submissionsConst,
      ).getV3CustomRules(subValues),
      ...this.getManufactureDateRules(subValues),
    ];
  }

  // Note: This method doesn't set a rule, but forces the subValues to be updated
  // It'd be better to put this in the submission detail component,
  // however, we'd have to add a bunch of special handling logic to avoid circular updates
  // so this works for now.
  private setEnergyEfficiencyValue(subValues: FormGroup): void {
    const designChoices: DesignChoices = {
      climateZone: subValues.get('climateZone')?.value,
      primarySpaceHeatingCooling: subValues.get('primarySpaceHeatingCooling')
        ?.value,
      envelope: subValues.get('envelope')?.value,
      lighting: subValues.get('lighting')?.value,
      appliances: subValues.get('appliances')?.value,
      estarClothesWasher: subValues.get('estarClothesWasher')?.value,
      bathroomWaterFixtures: subValues.get('bathroomWaterFixtures')?.value,
      waterHeating: subValues.get('waterHeating')?.value,
    };

    const points = DesignPointsService.calculateDesignPoints(designChoices);
    subValues.patchValue(
      { energyEfficiencyValue: points },
      { emitEvent: false },
    );
  }

  private getShouldShowDownloadInvoiceRules(subValues: FormGroup): FormRule[] {
    const heatPumpInvoiceStatus = subValues.get('heatPumpInvoiceStatus')?.value;
    return [
      this.ruleFact.createRule(`${heatPumpInvoiceStatus === 'present'}`, [
        this.createBaseEffect(
          RuleAction.MAKE_VISIBLE,
          'downloadHeatPumpInvoice',
        ),
        this.createBaseEffect(
          RuleAction.MAKE_HIDDEN,
          'heatPumpInvoiceNotAvailable',
        ),
        this.createBaseEffect(
          RuleAction.MAKE_HIDDEN,
          'heatPumpInvoiceErrorMessage',
        ),
      ]),
      this.ruleFact.createRule(`${heatPumpInvoiceStatus === 'error'}`, [
        this.createBaseEffect(
          RuleAction.MAKE_HIDDEN,
          'downloadHeatPumpInvoice',
        ),
        this.createBaseEffect(
          RuleAction.MAKE_HIDDEN,
          'heatPumpInvoiceNotAvailable',
        ),
        this.createBaseEffect(
          RuleAction.MAKE_VISIBLE,
          'heatPumpInvoiceErrorMessage',
        ),
        this.createBaseEffect(
          RuleAction.SET_WARNING_MESSAGE,
          'heatPumpInvoiceErrorMessage',
          'Error retrieving invoice information. Please contact support.',
        ),
      ]),
      this.ruleFact.createRule(
        `${heatPumpInvoiceStatus !== 'present' && heatPumpInvoiceStatus !== 'error'}`,
        [
          this.createBaseEffect(
            RuleAction.MAKE_HIDDEN,
            'downloadHeatPumpInvoice',
          ),
          this.createBaseEffect(
            RuleAction.MAKE_VISIBLE,
            'heatPumpInvoiceNotAvailable',
          ),
          this.createBaseEffect(
            RuleAction.MAKE_HIDDEN,
            'heatPumpInvoiceErrorMessage',
          ),
        ],
      ),
    ];
  }
  private getManufactureDateRules(subValues: FormGroup): FormRule[] {
    const createHomeManufacturingDateEffect = (
      action: RuleAction,
      value?: string,
    ) => this.createBaseEffect(action, 'homeManufacturedDate', value);

    const status = subValues.get('status')?.value;
    const homeManufacturedDate = subValues.get('homeManufacturedDate')?.value;

    const shouldShowError =
      !homeManufacturedDate ||
      new Date(homeManufacturedDate) < new Date('1/1/2023');

    const shouldShowWarning =
      !!homeManufacturedDate &&
      new Date(homeManufacturedDate) >= new Date('1/1/2023') &&
      new Date(homeManufacturedDate) <= new Date('7/10/2023');

    const shouldShowNothing = !shouldShowError && !shouldShowWarning;

    // Set a form-level error so submission is blocked even if the target control is disabled.
    const shouldBlockReadyToSubmit =
      status === 'Ready To Submit' && shouldShowError;
    this.setFormError(
      subValues,
      'homeManufacturedDateInvalid',
      shouldBlockReadyToSubmit,
    );

    return [
      this.ruleFact.createRule(`${shouldShowNothing}`, [
        createHomeManufacturingDateEffect(RuleAction.CLEAR_ERROR_MESSAGE),
      ]),
      this.ruleFact.createRule(`${shouldShowWarning}`, [
        createHomeManufacturingDateEffect(
          RuleAction.SET_WARNING_MESSAGE,
          this.submissionsConst.zerhGoLiveWarning,
        ),
      ]),
      this.ruleFact.createRule(`${shouldShowError}`, [
        createHomeManufacturingDateEffect(RuleAction.MAKE_INVALID),
        createHomeManufacturingDateEffect(RuleAction.MAKE_REQUIRED),
        createHomeManufacturingDateEffect(
          RuleAction.SET_ERROR_MESSAGE,
          this.submissionsConst.manufactureDateError,
        ),
      ]),
    ];
  }

  private setFormError(form: FormGroup, key: string, shouldSet: boolean): void {
    const currentErrors = { ...(form.errors ?? {}) };

    if (shouldSet) {
      form.setErrors({ ...currentErrors, [key]: true });
      return;
    }

    if (!Object.hasOwn(currentErrors, key)) {
      return;
    }

    delete currentErrors[key];
    form.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
  }

  private createBaseEffect(
    action: RuleAction,
    controlToApplyTo: string,
    value?: string,
  ): FormRuleEffect {
    return {
      action: action,
      affectingType: 'control',
      applyTo: controlToApplyTo,
      value: value,
    };
  }
}
